/**
 * The member's private conversation with the Elyxeer team.
 *
 * Transport and cache behaviour live here; the transcript and composer are shared
 * design-system primitives, which keeps the visual chat reusable by the admin app.
 */

import {
  ChatComposer,
  ChatMessageList,
  ErrorState,
  LoadingState,
  RADIUS,
} from "@elyxeer/ui";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import { fetchChatThread, markChatRead, sendChatMessage } from "~/lib/api";
import { uniqueChatMessages, withAutoReplies } from "~/lib/chat";
import { ensureNotificationPermission } from "~/lib/push-notifications";
import { queryKeys } from "~/lib/query-client";
import { AnalyticsEventName } from "~/types/analytics";
import type { ChatThread } from "~/types/chat";

const MESSAGE_PAGE_SIZE = 50;
const CHAT_POLL_INTERVAL_MS = 5_000;

interface TeamChatDialogProps {
  open: boolean;
  userId: string;
  onClose: () => void;
}

export function TeamChatDialog({ open, userId, onClose }: TeamChatDialogProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [sendErrorKey, setSendErrorKey] = useState<string | null>(null);
  const chatQueryKey = queryKeys.chatThread(userId);

  const attemptedReadMarker = useRef<string | null>(null);

  const thread = useInfiniteQuery({
    queryKey: chatQueryKey,
    queryFn: ({ pageParam, signal }) =>
      fetchChatThread({
        limit: MESSAGE_PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.messages.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: open,
    staleTime: 0,
    // A short poll is enough for a personal conversation and avoids introducing a
    // second realtime transport solely for a low-volume chat.
    refetchInterval: open ? CHAT_POLL_INTERVAL_MS : false,
  });

  const { mutate: markRead } = useMutation({
    mutationFn: markChatRead,
    onSuccess: () => {
      queryClient.setQueryData<InfiniteData<ChatThread, number>>(
        chatQueryKey,
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  unread_count: 0,
                  messages: page.messages.map((message) =>
                    message.sender_role === "admin" && !message.read_at
                      ? { ...message, read_at: new Date().toISOString() }
                      : message,
                  ),
                })),
              }
            : current,
      );
    },
  });

  const send = useMutation({
    mutationFn: sendChatMessage,
    onSuccess: (message) => {
      queryClient.setQueryData<InfiniteData<ChatThread, number>>(
        chatQueryKey,
        (current) => {
          if (!current?.pages[0]) return current;
          const exists = current.pages.some((page) =>
            page.messages.some(({ id }) => id === message.id),
          );
          return {
            ...current,
            pages: current.pages.map((page, index) => ({
              ...page,
              total: page.total + (exists ? 0 : 1),
              updated_at: message.created_at,
              messages:
                index === 0 && !exists
                  ? [...page.messages, message].slice(-page.limit)
                  : page.messages,
            })),
          };
        },
      );
      // Re-page every loaded offset: a new newest message shifts all older boundaries.
      void queryClient.invalidateQueries({ queryKey: chatQueryKey });
    },
  });

  useEffect(() => {
    if (!open) return;
    // The closed→open edge, so one call site covers both the profile row and the
    // `?chat=open` deep link a push notification arrives on.
    track(AnalyticsEventName.ChatOpen, { subjectType: "chat" });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Native only. On the web this resolves immediately and never asks the browser for
    // permission; declining notifications must never block the conversation itself.
    // One permission covers every push the app sends, so both channels are described
    // here even though only chat is on screen — Android lists them the moment the
    // member opens the app's notification settings.
    void ensureNotificationPermission({
      userId,
      channels: {
        chat: {
          name: t("profile.chat.notificationChannel"),
          description: t("profile.chat.notificationChannelDescription"),
        },
        eventReminders: {
          name: t("notifications.channel.eventReminders"),
          description: t("notifications.channel.eventRemindersDescription"),
        },
      },
    }).catch(() => undefined);
  }, [open, t, userId]);

  const detail = thread.data?.pages[0];
  const latestUnreadId = detail?.messages
    .filter(
      (message) => message.sender_role === "admin" && message.read_at === null,
    )
    .at(-1)?.id;
  const readMarker =
    detail && detail.unread_count > 0
      ? (latestUnreadId ?? detail.updated_at)
      : null;

  useEffect(() => {
    if (!open || !readMarker || attemptedReadMarker.current === readMarker)
      return;
    attemptedReadMarker.current = readMarker;
    markRead();
  }, [markRead, open, readMarker]);

  const messages = useMemo(
    () =>
      withAutoReplies(uniqueChatMessages(thread.data?.pages ?? [])).map(
        (item) => {
          if (item.kind === "auto-reply") {
            return {
              id: item.id,
              body: t("profile.chat.autoReply"),
              createdAt: item.created_at,
              isOwn: false,
              senderLabel: t("profile.chat.autoReplyLabel"),
            };
          }
          const { message } = item;
          return {
            id: message.id,
            body: message.body,
            createdAt: message.created_at,
            isOwn: message.sender_role === "member",
            senderLabel:
              message.sender_role === "admin"
                ? (message.sender_display_name ?? t("profile.chat.specialist"))
                : null,
          };
        },
      ),
    [t, thread.data?.pages],
  );

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    setSendErrorKey(null);
    try {
      await send.mutateAsync(body);
      setDraft("");
    } catch {
      setSendErrorKey("profile.chat.sendError");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="team-chat-title"
      maxWidth={false}
      data-testid="chat.dialog"
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            width: { xs: "100%", sm: 560 },
            maxWidth: "100%",
            height: { xs: "100%", sm: "min(760px, calc(100% - 64px))" },
            maxHeight: "100%",
            margin: { xs: 0, sm: 4 },
            borderRadius: {
              xs: 0,
              sm: `${RADIUS.lg}px`,
            },
            backgroundColor: "background.paper",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          paddingTop: "calc(12px + env(safe-area-inset-top))",
          paddingBottom: 1.5,
          paddingInline: { xs: 2, sm: 2.5 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="team-chat-title" variant="H3">
            {t("profile.chat.title")}
          </Typography>
          <Typography
            variant="T3"
            sx={{ color: "text.muted", marginTop: 0.25 }}
          >
            {t("profile.chat.subtitle")}
          </Typography>
        </Box>
        <IconButton aria-label={t("profile.chat.close")} onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
        }}
      >
        {thread.isPending ? (
          <Box sx={{ display: "grid", flex: 1, placeItems: "center" }}>
            <LoadingState label={t("profile.chat.loading")} />
          </Box>
        ) : thread.isError ? (
          <Box sx={{ display: "grid", flex: 1, placeItems: "center" }}>
            <ErrorState
              message={t("profile.chat.loadError")}
              onRetry={() => void thread.refetch()}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              flexDirection: "column",
            }}
          >
            {thread.hasNextPage ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: 1,
                }}
              >
                <Button
                  size="small"
                  onClick={() => void thread.fetchNextPage()}
                  disabled={thread.isFetchingNextPage}
                  data-testid="chat.load-earlier"
                >
                  {t(
                    thread.isFetchingNextPage
                      ? "profile.chat.loadingEarlier"
                      : "profile.chat.loadEarlier",
                  )}
                </Button>
              </Box>
            ) : null}
            <ChatMessageList
              messages={messages}
              emptyMessage={t("profile.chat.empty")}
              formatTime={(value) =>
                formatDate(value, {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }
              ariaLabel={t("profile.chat.transcript")}
            />
          </Box>
        )}

        <ChatComposer
          value={draft}
          onChange={(value) => {
            setDraft(value);
            setSendErrorKey(null);
          }}
          onSend={() => void handleSend()}
          placeholder={t("profile.chat.placeholder")}
          sendLabel={t("profile.chat.send")}
          disabled={thread.isPending || thread.isError}
          isSending={send.isPending}
          error={sendErrorKey ? t(sendErrorKey) : null}
          testId="chat.composer"
        />
      </DialogContent>
    </Dialog>
  );
}
